import { SignInButton, SignUpButton } from "@clerk/clerk-react";
import { AlertCircle, Loader, Mail, Users } from "lucide-react";
import type React from "react";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { apiClient } from "../lib/apiClient";

interface InviteDetails {
	organisationName: string;
	email: string;
}

export default function AcceptInvite() {
	const [searchParams] = useSearchParams();
	const token = searchParams.get("token");

	const [invite, setInvite] = useState<InviteDetails | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (!token) {
			setError("No invite token provided. Please check your invitation email.");
			setIsLoading(false);
			return;
		}

		const validateToken = async () => {
			try {
				const response = await apiClient().get<InviteDetails>(
					`/team/invite/validate?token=${encodeURIComponent(token)}`,
				);
				setInvite(response.data);
			} catch (err: unknown) {
				const axiosError = err as { response?: { status?: number } };
				if (axiosError.response?.status === 410) {
					setError("This invitation has expired or has already been accepted.");
				} else if (axiosError.response?.status === 404) {
					setError("Invitation not found. Please request a new invitation.");
				} else {
					setError("Failed to validate your invitation. Please try again.");
				}
			} finally {
				setIsLoading(false);
			}
		};

		validateToken();
	}, [token]);

	if (isLoading) {
		return (
			<div className="min-h-screen bg-gradient-to-br from-pink-50 via-orange-50 to-yellow-50 flex items-center justify-center">
				<div className="text-center">
					<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-400 mx-auto mb-4" />
					<p className="text-gray-600">Validating your invitation...</p>
				</div>
			</div>
		);
	}

	if (error || !invite) {
		return (
			<div className="min-h-screen bg-gradient-to-br from-pink-50 via-orange-50 to-yellow-50 flex items-center justify-center">
				<div className="text-center p-8 max-w-md">
					<AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
					<h2 className="text-2xl font-bold text-gray-900 mb-2">Invalid Invitation</h2>
					<p className="text-gray-600">{error ?? "This invitation link is no longer valid."}</p>
				</div>
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-gradient-to-br from-pink-50 via-orange-50 to-yellow-50 flex items-center justify-center">
			<div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
				<div className="max-w-md mx-auto">
					<div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-sm border border-gray-200 p-6 sm:p-8">
						{/* Logo / icon */}
						<div className="flex justify-center mb-6">
							<div className="w-16 h-16 bg-gradient-to-br from-orange-400 to-pink-400 rounded-2xl flex items-center justify-center shadow-md">
								<Users className="w-8 h-8 text-white" />
							</div>
						</div>

						{/* Heading */}
						<div className="text-center mb-6">
							<h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
								You've been invited
							</h1>
							<p className="text-gray-600 text-sm sm:text-base">
								Join <span className="font-semibold text-gray-900">{invite.organisationName}</span>{" "}
								on Sehara
							</p>
						</div>

						{/* Invited email */}
						<div className="flex items-center gap-3 bg-gray-50 rounded-lg px-4 py-3 mb-6">
							<Mail className="w-5 h-5 text-gray-400 flex-shrink-0" />
							<span className="text-sm text-gray-700 truncate">{invite.email}</span>
						</div>

						{/* CTAs */}
						<div className="space-y-3">
							<SignUpButton mode="redirect" forceRedirectUrl="/invite-accepted">
								<button
									type="button"
									className="w-full bg-gradient-to-r from-orange-400 to-pink-400 hover:from-orange-500 hover:to-pink-500 text-white font-semibold py-4 px-6 rounded-lg transition-all duration-300 shadow-sm text-base touch-manipulation min-h-[56px]"
								>
									Create account
								</button>
							</SignUpButton>
						</div>

						<p className="text-center text-xs text-gray-400 mt-6">
							By creating an account you agree to our terms of service and privacy policy.
						</p>
					</div>
				</div>
			</div>
		</div>
	);
}
